import { skipToken, useQuery } from '@tanstack/react-query';
import { keyBy } from 'lodash-es';
import { cdpDestinationOptionsApi } from './client';
import { cdpDestinationOptionKeys, type AvailableDestination } from './entity';

// Every read is the non-suspending `…Optional` form: its callers mount before
// the workspace or option settles. While an id is null the read parks on a
// sentinel key with `skipToken` (see `useDomainsOptional`).

const STALE_TIME = 60 * 1000;

const byOptionId = (options: AvailableDestination[]) => keyBy(options, 'option_id');

export const useDestinationOptionsOptional = ({ workspaceId }: { workspaceId: number | null }) =>
  useQuery({
    queryKey:
      workspaceId != null ? cdpDestinationOptionKeys.lists(workspaceId) : cdpDestinationOptionKeys.inactive('list'),
    queryFn: workspaceId != null ? () => cdpDestinationOptionsApi.list(workspaceId) : skipToken,
    staleTime: STALE_TIME,
  });

/** The same cached catalog, keyed by `option_id`. */
export const useDestinationOptionsMapOptional = ({ workspaceId }: { workspaceId: number | null }) =>
  useQuery({
    queryKey:
      workspaceId != null ? cdpDestinationOptionKeys.lists(workspaceId) : cdpDestinationOptionKeys.inactive('list'),
    queryFn: workspaceId != null ? () => cdpDestinationOptionsApi.list(workspaceId) : skipToken,
    staleTime: STALE_TIME,
    select: byOptionId,
  });

export const useDestinationOptionManifestOptional = ({
  workspaceId,
  optionId,
}: {
  workspaceId: number | null;
  optionId: string | null | undefined;
}) => {
  const active = workspaceId != null && optionId != null;
  return useQuery({
    queryKey: active
      ? cdpDestinationOptionKeys.manifest(workspaceId, optionId)
      : cdpDestinationOptionKeys.inactive('manifest'),
    queryFn: active ? () => cdpDestinationOptionsApi.manifest(workspaceId, optionId) : skipToken,
    staleTime: STALE_TIME,
  });
};
